import type { HomeyAPI, User } from 'homey-api';
import { unstable_batchedUpdates } from 'react-dom';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

import { userMeDraftStore } from './UserMeDraftStore.ts';

export type UserProps = {
  [K in keyof User as User[K] extends Function ? never : K]: User[K];
};

type UserMeData = User;
type UserMe = UserProps;

interface State {
  data: UserMeData | null;
  userMe: UserMe | null;
  isLoading: boolean;
  error: unknown;
}

export type UserMeStoreState = State;

export class UserMeStore extends BaseApiStore<State, User> {
  constructor() {
    super('user-me');
  }

  override createInitialState() {
    return {
      data: null,
      userMe: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.users, reference: this });

      if (abortController.signal.aborted) return;

      const userMePromise = api.users.getUserMe({ $cache: cache });

      const [userMeData] = await Promise.all([userMePromise]);

      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        data: userMeData,
        userMe: { ...userMeData },
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.users, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.users.on('user.update', this.handleUpdate);
    api.users.on('user.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.users.off('user.update', this.handleUpdate);
    api.users.off('user.delete', this.handleDelete);
  }

  getInstanceFromCache(user: { id: string }) {
    return this.getCache()[user.id];
  }

  getCache(): { [key: string]: User } {
    const api = this.getApi();
    const cache = api.users.__cache['user'];
    return cache;
  }

  handleUpdate = (updatedUser: User) => {
    this.DEBUG && console.info(`update:user:${updatedUser.id}`);
    const userMeId = this.get().data?.id;
    const userMe = this.get().userMe;

    if (updatedUser.id !== userMeId) {
      return;
    }

    if (updatedUser.role !== userMe?.role) {
      this.DEBUG && console.info(`update:user-me:role-changed`);
      // TODO
    }

    if (userMe?.enabled === true && updatedUser.enabled === false) {
      this.DEBUG && console.info(`update:user-me:disabled`);
      // TODO
    }

    this.addToQueue(updatedUser.id, updatedUser);

    this.batch((updateQueue) => {
      const state = this.get();
      const queuedUserMe = updateQueue[userMeId];
      const userMeInstance = this.getInstanceFromCache(queuedUserMe);

      if (userMeInstance == null) {
        this.DEBUG && console.info(`update:user-me:missing-data`);
        return;
      }

      if (userMeInstance == null || state.data == null) {
        this.DEBUG && console.info(`update:user-me:unknown`);
        return;
      }

      const userMeReference = { ...userMeInstance };

      unstable_batchedUpdates(() => {
        this.set({
          userMe: userMeReference,
        });
        userMeDraftStore.notify();
      });
    });
  };

  handleDelete = (deletedUser: User) => {
    this.DEBUG && console.info(`delete:user:${deletedUser.id}`);
    const userMeId = this.get().data?.id;

    if (deletedUser.id !== userMeId) {
      return;
    }

    unstable_batchedUpdates(() => {
      userMeDraftStore.notify();
      this.set({
        data: null,
        userMe: null,
      });
    });
  };

  restore() {
    const id = this.get().data?.id;

    if (id == null) {
      return;
    }

    const userMeData = this.getInstanceFromCache({ id });

    this.set({
      data: userMeData,
      userMe: { ...userMeData },
    });
  }
}

export const userMeStore = new UserMeStore();
